import React, { useState } from 'react';
import Modal from './Modal';
import './ColumnSettings.css';

/**
 * 列設定コンポーネント
 * 列の表示/非表示、順序変更、ソート設定を統一的に管理
 * 
 * @param {Object} props
 * @param {Array} props.columns - 列定義の配列 [{id: 'column_id', label: '列名', sortable: true/false, visible: true/false, fixed: true/false}, ...]
 * @param {Function} props.onColumnsChange - 列設定変更時のコールバック
 * @param {Object} props.sortConfig - 現在のソート設定 {column: 'column_id', order: 'asc'|'desc'}
 * @param {Function} props.onSortChange - ソート変更時のコールバック
 * @param {Function} props.onReset - リセット時のコールバック
 * @param {Boolean} props.isOpen - パネルの開閉状態
 * @param {Function} props.onToggle - パネル開閉のトグル
 */
const ColumnSettings = ({ 
  columns, 
  onColumnsChange, 
  sortConfig = null, 
  onSortChange = null,
  onReset,
  isOpen = false,
  onToggle
}) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // ドラッグ開始
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  // ドラッグオーバー
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  // ドラッグ終了
  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // ドロップ
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newColumns = [...columns];
    const [draggedColumn] = newColumns.splice(draggedIndex, 1);
    newColumns.splice(dropIndex, 0, draggedColumn);

    onColumnsChange(newColumns);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // 表示/非表示の切り替え
  const handleVisibilityToggle = (columnId) => {
    const newColumns = columns.map(col => 
      col.id === columnId ? { ...col, visible: !col.visible } : col
    );
    onColumnsChange(newColumns);
  };

  // すべて表示
  const handleShowAll = () => {
    const newColumns = columns.map(col => ({ ...col, visible: true }));
    onColumnsChange(newColumns);
  };

  // すべて非表示（固定列を除く）
  const handleHideAll = () => {
    const newColumns = columns.map(col => 
      col.fixed ? col : { ...col, visible: false }
    );
    onColumnsChange(newColumns);
  };

  // 列設定が開いていない場合は何も表示しない
  if (!isOpen) return null;

  return (
    <Modal
      title="列設定"
      onClose={onToggle}
      width="600px"
      className="column-settings-modal"
    >
        <div className="column-settings-header-in-panel">
          <button className="btn btn-sm btn-secondary" onClick={handleShowAll}>
            すべて表示
          </button>
          <button className="btn btn-sm btn-secondary" onClick={handleHideAll}>
            すべて非表示
          </button>
          <button className="btn btn-sm btn-danger" onClick={onReset}>
            🔄 リセット
          </button>
        </div>

        <div className="column-settings-info">
          <span>💡 ドラッグ＆ドロップで列の順序を変更できます</span>
        </div>

        <div className="column-list">
          {columns.map((column, index) => (
            <div
              key={column.id}
              className={`column-item ${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''} ${column.fixed ? 'fixed' : ''}`}
              draggable={!column.fixed}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              onDrop={(e) => handleDrop(e, index)}
            >
              <div className="column-item-drag-handle">
                {column.fixed ? '🔒' : '☰'}
              </div>
              <label className="column-item-label">
                <input
                  type="checkbox"
                  checked={column.visible}
                  onChange={() => !column.fixed && handleVisibilityToggle(column.id)}
                  disabled={column.fixed}
                />
                <span className={column.visible ? '' : 'hidden-label'}>
                  {column.label}
                </span>
              </label>
              {column.sortable && sortConfig && onSortChange && (
                <div className="column-item-sort">
                  <button
                    className={`sort-btn ${sortConfig.column === column.id && sortConfig.order === 'asc' ? 'active' : ''}`}
                    onClick={() => onSortChange({ column: column.id, order: 'asc' })}
                    title="昇順"
                  >
                    ▲
                  </button>
                  <button
                    className={`sort-btn ${sortConfig.column === column.id && sortConfig.order === 'desc' ? 'active' : ''}`}
                    onClick={() => onSortChange({ column: column.id, order: 'desc' })}
                    title="降順"
                  >
                    ▼
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
    </Modal>
  );
};

export default ColumnSettings;
